import { Box, Button, Typography } from '@mui/material';
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
    children: ReactNode;
}

interface ErrorBoundaryState {
    message: string | null;
}

/**
 * Keeps a render error inside one route instead of white-screening the app.
 * Class component because React exposes no hook equivalent.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    public override state: ErrorBoundaryState = { message: null };

    public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { message: error.message };
    }

    public override componentDidCatch(error: Error, info: ErrorInfo): void {
        // No external logger is configured; the console is the only sink.
        console.error('Render failed', error, info.componentStack);
    }

    public override render(): ReactNode {
        if (this.state.message === null) return this.props.children;

        return (
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="h3">That screen broke</Typography>
                <Typography color="text.secondary">{this.state.message}</Typography>
                <Button variant="contained" onClick={() => window.location.reload()}>
                    Reload
                </Button>
            </Box>
        );
    }
}
